import React, { useState } from 'react'
import { makeStyles, Popover } from '@material-ui/core'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import WorkOutlineIcon from '@material-ui/icons/WorkOutline'
import SettingsInputAntennaIcon from '@material-ui/icons/SettingsInputAntenna'
import BarChartIcon from '@material-ui/icons/BarChart'
import VideocamIcon from '@material-ui/icons/Videocam'
import { useMyLayout } from '../../context/useMyLayout'
import useGetLocalStorage from '../../hooks/useGetLocalStorage'
import localStorageIds from '../../utils/constants/localStorageIds'
import USER_ORG_ROLES from '../../utils/constants/userOrgRoles'
import { DUTIES, normaliseDuties } from '../../utils/constants/permissionSets'
import roleCardStyles from './roleCard.styles'

const HELP_URL = 'https://docs.mismatch.gr/platform/#organization-users'

const ROLE_ICONS = {
  [USER_ORG_ROLES.OWNER]: LockOutlinedIcon,
  [USER_ORG_ROLES.ADMIN]: VerifiedUserOutlinedIcon,
  [USER_ORG_ROLES.MEMBER]: PersonOutlineIcon,
}

const DUTY_ICONS = {
  Manager: WorkOutlineIcon,
  StudioOperator: SettingsInputAntennaIcon,
  Statistician: BarChartIcon,
  VideoOperator: VideocamIcon,
}

const useStyles = makeStyles(roleCardStyles)

// The user's role in the selected org and, for a Member, the duties (permission sets) that decide
// what they see. Read from /users/me, which the layout already holds.
const RoleCard = () => {
  const classes = useStyles()
  const { user } = useMyLayout()
  const localStorageValue = useGetLocalStorage()
  const selectedOrg = localStorageValue?.[localStorageIds.SELECTED_ORG]
  const [helpAnchor, setHelpAnchor] = useState(null)

  const role = user?.orgRoles?.[selectedOrg?.id]
  // A SuperAdmin browsing an org they don't belong to has no role there.
  if (!role) return null

  const isMember = role === USER_ORG_ROLES.MEMBER
  const dutyIds = isMember ? normaliseDuties(user?.permissionSets?.[selectedOrg.id]) : []
  const duties = DUTIES.filter((duty) => dutyIds.includes(duty.id))
  const RoleIcon = ROLE_ICONS[role]
  const showsNoDuties = isMember && !duties.length

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <div className={classes.avatar}>{RoleIcon && <RoleIcon className={classes.avatarIcon} />}</div>
        <div className={classes.text}>
          <span className={classes.role}>
            {role}
            {role !== USER_ORG_ROLES.OWNER && (
              <button
                type='button'
                aria-label='About your role'
                className={classes.helpButton}
                onClick={(e) => setHelpAnchor(helpAnchor ? null : e.currentTarget)}
              >
                <InfoOutlinedIcon className={classes.helpIcon} />
              </button>
            )}
          </span>
          <span className={classes.org}>{selectedOrg.name}</span>
        </div>
      </div>
      <Popover
        open={Boolean(helpAnchor)}
        anchorEl={helpAnchor}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.popover }}
      >
        <a className={classes.help} href={HELP_URL} target='_blank' rel='noopener noreferrer'>
          What can I do here?
          <OpenInNewIcon className={classes.helpLinkIcon} />
        </a>
      </Popover>
      {showsNoDuties && (
        <span className={classes.description}>No duties yet, so no events are assigned to you.</span>
      )}
      {duties.length > 0 && (
        <div className={classes.duties}>
          {duties.map(({ id, label }) => {
            const Icon = DUTY_ICONS[id]
            return (
              <span key={id} className={classes.duty}>
                <span className={classes.dutyIconColumn}>
                  <Icon className={classes.dutyIcon} />
                </span>
                {label}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default RoleCard
